import { Link, useLocation } from 'react-router-dom';
import { ChevronLeft, FileQuestion, Home, Mail, Search } from 'lucide-react';
import ScrollReveal from '../../components/public/ScrollReveal.jsx';
import SectionAmbientGradient from '../../components/public/SectionAmbientGradient.jsx';
import { PUBLIC_NAV_ITEMS } from '../../constants/navigation.js';
import { usePageSeo } from '../../hooks/usePageSeo.js';

const QUICK_LINKS = [
  { to: '/', label: 'Inicio', icon: Home },
  { to: '/contacto', label: 'Contacto / PQRS', icon: Mail },
  { to: '/creditos', label: 'Créditos', icon: Search },
];

export default function NotFoundPage() {
  const { pathname } = useLocation();

  usePageSeo({
    title: 'Página no encontrada',
    description: 'La dirección que buscas no existe o fue movida. Vuelve al inicio o usa el menú del portal Coonaltragas.',
    noIndex: true,
  });

  return (
    <div className="pb-20">
      <section className="relative overflow-hidden border-b border-white/10">
        <SectionAmbientGradient />
        <div className="relative mx-auto max-w-7xl px-6 py-16 text-center lg:px-8 lg:py-24">
          <ScrollReveal>
            <p className="font-display text-7xl font-bold tracking-tight text-white md:text-8xl">404</p>
            <p className="mt-4 text-xs font-semibold uppercase tracking-[0.32em] text-accent-lime">
              Página no encontrada
            </p>
            <h1 className="mt-6 font-display text-3xl font-bold text-white md:text-4xl">
              No encontramos esta sección
            </h1>
            <p className="mx-auto mt-6 max-w-xl text-lg leading-relaxed text-slate-300">
              La ruta <span className="font-mono text-sm text-accent-lime">{pathname}</span> no existe, fue
              eliminada o escribiste mal la dirección. Prueba con los enlaces siguientes.
            </p>
            <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link
                to="/"
                className="inline-flex items-center gap-2 rounded-lg bg-[#1acb12] px-6 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-[#17b810]"
              >
                <Home className="h-4 w-4" aria-hidden />
                Ir al inicio
              </Link>
              <Link
                to="/contacto"
                className="inline-flex items-center gap-2 rounded-lg border border-white/25 bg-white/10 px-6 py-3 text-sm font-semibold text-white transition hover:bg-white/15"
              >
                <Mail className="h-4 w-4" aria-hidden />
                Contacto
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <section className="border-b border-slate-200 bg-white py-14 lg:py-20">
        <div className="mx-auto max-w-4xl px-6 lg:px-8">
          <ScrollReveal>
            <div className="flex items-start gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-6 ring-1 ring-slate-100">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#0d2b7f]/10 text-[#0d2b7f] ring-1 ring-[#0d2b7f]/15">
                <FileQuestion className="h-6 w-6" strokeWidth={1.75} aria-hidden />
              </span>
              <div>
                <h2 className="font-display text-lg font-semibold text-[#0d2b7f]">Enlaces útiles</h2>
                <p className="mt-2 text-sm text-slate-600">
                  Estas son las secciones principales del portal para asociados.
                </p>
                <ul className="mt-5 grid gap-2 sm:grid-cols-2">
                  {QUICK_LINKS.map((link) => (
                    <li key={link.to}>
                      <Link
                        to={link.to}
                        className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:border-[#1acb12]/40 hover:text-[#0d2b7f]"
                      >
                        <link.icon className="h-4 w-4 text-[#00A63E]" aria-hidden />
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </ScrollReveal>

          <ScrollReveal delayMs={80} className="mt-8">
            <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Menú del sitio</h3>
            <ul className="mt-4 flex flex-wrap gap-x-2 gap-y-3 pb-2">
              {PUBLIC_NAV_ITEMS.flatMap((item) =>
                item.type === 'dropdown'
                  ? item.items.map((sub) => (
                      <li key={sub.to} className="mb-1">
                        <Link
                          to={sub.to}
                          className="inline-flex rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 transition hover:border-[#1acb12]/40 hover:text-[#0d2b7f]"
                        >
                          {sub.label}
                        </Link>
                      </li>
                    ))
                  : [
                      <li key={item.to} className="mb-1">
                        <Link
                          to={item.to}
                          className="inline-flex rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 transition hover:border-[#1acb12]/40 hover:text-[#0d2b7f]"
                        >
                          {item.label}
                        </Link>
                      </li>,
                    ]
              )}
            </ul>
            <Link
              to="/"
              className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-accent-lime hover:text-[#0d2b7f]"
            >
              <ChevronLeft className="h-4 w-4" aria-hidden />
              Volver al inicio
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}
